import type { CSSProperties } from 'react'
import { forwardRef } from 'react'

import { type SpacingProps, type TypographyProps } from '@material-ui/system'
import { type theme } from '@procurify/ui-theme'
import classNames from 'classnames'

import './fonts/procurify/icons.scss'
import { type IconTypes } from './iconTypes'
import { IconStyled } from './styles/IconStyled'

type ThemeColors = (typeof theme)['colors']

// Only color tokens that resolve to a string are valid Icon colors; the nested
// token groups (e.g. `button`, `badge`) are objects and can't be a CSS colour.
type ColorToken = {
  [K in keyof ThemeColors]: ThemeColors[K] extends string ? K : never
}[keyof ThemeColors]

export interface IIconProps extends TypographyProps, SpacingProps {
  ariaLabel?: string
  color?: ColorToken
  icon: IconTypes
  size?: number
  className?: any
  style?: CSSProperties
  bold?: boolean
}

const IconBase = forwardRef<HTMLElement, IIconProps>(
  ({ icon, className, color, size, ariaLabel, ...otherProps }, ref) => {
    if (!icon) {
      return null
    }

    return (
      <IconStyled
        ref={ref}
        className={classNames(`icon-${icon}`, className)}
        color={color}
        size={size}
        aria-label={ariaLabel}
        role='img'
        {...otherProps}
      />
    )
  }
)

IconBase.displayName = 'Icon'

export const Icon = Object.assign(IconBase, { Styled: IconStyled })
